import { useState } from "react";
import { toast } from "sonner";

import { autoCompleteFor, inputModeFor } from "@/lib/form-a11y";
import { supabase } from "@/integrations/supabase/client";

const inputClass =
  "mt-1 w-full rounded-lg border border-border bg-muted/40 px-3 py-2 text-base text-foreground outline-none focus:ring-2 focus:ring-accent";
const hintClass = "mt-1 block font-sans text-sm font-semibold text-foreground";

const PURPOSES = [
  "General Fund",
  "Crusades",
  "Compassion Outreach",
  "Mission Trip",
  "Where Most Needed",
];

const COUNTRIES = ["United States", "Canada", "Mexico", "Bolivia", "Honduras", "Other"];

/**
 * Donation request form used by /donation-form and
 * /donation-form/monthly-recurring. Card details are never collected here —
 * the ministry follows up with a secure payment link.
 */
export function DonationForm({ frequency }: { frequency: "one-time" | "monthly" }) {
  const [submitting, setSubmitting] = useState(false);
  const [status, setStatus] = useState("");

  async function handleSubmit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const form = event.currentTarget;
    const data = new FormData(form);
    const value = (key: string) => String(data.get(key) ?? "").trim();

    const amount = Number(value("amount").replace(/[^0-9.]/g, ""));
    if (!Number.isFinite(amount) || amount <= 0) {
      toast.error("Please enter a valid donation amount.");
      setStatus("Please enter a valid donation amount.");
      return;
    }

    setSubmitting(true);
    const { error } = await supabase.from("donation_requests").insert({
      first_name: value("first_name"),
      last_name: value("last_name"),
      email: value("email") || null,
      address1: value("address1") || null,
      address2: value("address2") || null,
      city: value("city") || null,
      state: value("state") || null,
      postal_code: value("postal_code") || null,
      country: value("country") || null,
      purpose: value("purpose"),
      amount,
      frequency,
    });
    setSubmitting(false);

    if (error) {
      toast.error("We couldn't submit your donation request. Please try again.");
      setStatus("We couldn't submit your donation request. Please try again.");
      return;
    }
    toast.success("Thank you! We'll email you a secure link to complete your gift.");
    setStatus("Thank you! We'll email you a secure link to complete your gift.");
    form.reset();
  }

  return (
    <form onSubmit={handleSubmit} className="mt-8">
      <div className="grid gap-6 sm:grid-cols-2">
        <div>
          <label htmlFor="first_name" className="font-sans font-semibold text-foreground">
            First Name <span className="text-[#e8615b]">*</span>
          </label>
          <input
            id="first_name"
            name="first_name"
            required
            className={inputClass}
            autoComplete={autoCompleteFor("first_name")}
            inputMode={inputModeFor("first_name")}
          />
        </div>
        <div>
          <label htmlFor="last_name" className="font-sans font-semibold text-foreground">
            Last Name <span className="text-[#e8615b]">*</span>
          </label>
          <input
            id="last_name"
            name="last_name"
            required
            className={inputClass}
            autoComplete={autoCompleteFor("last_name")}
            inputMode={inputModeFor("last_name")}
          />
        </div>
      </div>

      <div className="mt-6">
        <label htmlFor="email" className="font-sans font-semibold text-foreground">
          Email
        </label>
        <input
          id="email"
          name="email"
          type="email"
          placeholder="you@example.com"
          className={inputClass}
          autoComplete={autoCompleteFor("email")}
          inputMode={inputModeFor("email")}
        />
      </div>

      <div className="mt-6">
        <span className="font-sans font-semibold text-foreground">Address</span>
        <input
          id="address1"
          name="address1"
          aria-label="Address"
          className={inputClass}
          autoComplete={autoCompleteFor("address1")}
          inputMode={inputModeFor("address1")}
        />
        <label htmlFor="address1" className={hintClass}>
          Address
        </label>
      </div>

      <div className="mt-4">
        <input
          id="address2"
          name="address2"
          aria-label="Address 2"
          className={inputClass}
          autoComplete={autoCompleteFor("address2")}
          inputMode={inputModeFor("address2")}
        />
        <label htmlFor="address2" className={hintClass}>
          Address 2
        </label>
      </div>

      <div className="mt-4 grid gap-4 sm:grid-cols-3">
        <div>
          <input
            id="city"
            name="city"
            aria-label="City"
            className={inputClass}
            autoComplete={autoCompleteFor("city")}
            inputMode={inputModeFor("city")}
          />
          <label htmlFor="city" className={hintClass}>
            City
          </label>
        </div>
        <div>
          <input
            id="state"
            name="state"
            aria-label="State"
            className={inputClass}
            autoComplete={autoCompleteFor("state")}
            inputMode={inputModeFor("state")}
          />
          <label htmlFor="state" className={hintClass}>
            State
          </label>
        </div>
        <div>
          <input
            id="postal_code"
            name="postal_code"
            aria-label="Zip / Postal code"
            className={inputClass}
            autoComplete={autoCompleteFor("postal_code")}
            inputMode={inputModeFor("postal_code")}
          />
          <label htmlFor="postal_code" className={hintClass}>
            Zip/Postal
          </label>
        </div>
      </div>

      <div className="mt-4">
        <select
          id="country"
          name="country"
          aria-label="Country"
          defaultValue="United States"
          className={inputClass}
        >
          {COUNTRIES.map((country) => (
            <option key={country} value={country}>
              {country}
            </option>
          ))}
        </select>
        <label htmlFor="country" className={hintClass}>
          Country
        </label>
      </div>

      <div className="mt-6">
        <label htmlFor="purpose" className="font-sans font-semibold text-foreground">
          Purpose of Donation <span className="text-[#e8615b]">*</span>
        </label>
        <select id="purpose" name="purpose" required defaultValue="" className={inputClass}>
          <option value="" disabled>
            Select a purpose
          </option>
          {PURPOSES.map((purpose) => (
            <option key={purpose} value={purpose}>
              {purpose}
            </option>
          ))}
        </select>
      </div>

      <div className="mt-6">
        <label htmlFor="amount" className="font-sans font-semibold text-foreground">
          Donation Amount <span className="text-[#e8615b]">*</span>
        </label>
        <input
          id="amount"
          name="amount"
          required
          placeholder="100.00"
          className={inputClass}
          autoComplete={autoCompleteFor("amount")}
          inputMode={inputModeFor("amount")}
        />
      </div>

      <p className="mt-6 max-w-xl font-sans text-sm text-foreground/70">
        For your security we don't collect card details on this page. Submit the form and our team
        will email you a secure payment link
        {frequency === "monthly" ? " to set up your monthly gift." : " to complete your gift."}
      </p>

      <button
        type="submit"
        disabled={submitting}
        className="mt-6 rounded-lg bg-accent px-8 py-3 font-sans font-semibold text-navy transition hover:brightness-110 disabled:opacity-60"
      >
        {submitting ? "Submitting…" : "Submit"}
      </button>
      <p role="status" aria-live="polite" className="sr-only">
        {status}
      </p>
    </form>
  );
}
